import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "../ui";
import { updateTaxDetails } from "../../services/accountService";
import { digitsOnlyVatInput, isValidSaudiVat } from "../../utils/saudiVat";

const EMPTY_TAX_FORM = {
  vatNumber: "",
  buildingNumber: "",
  street: "",
  district: "",
  city: "",
  postalCode: "",
  additionalNumber: "",
};

function taxFormFromBusinessInfo(businessInfo) {
  const info = businessInfo || {};
  const address = info.nationalAddress || {};
  return {
    vatNumber: info.vatNumber || "",
    buildingNumber: address.buildingNumber || "",
    street: address.street || "",
    district: address.district || "",
    city: address.city || "",
    postalCode: address.postalCode || "",
    additionalNumber: address.additionalNumber || "",
  };
}

const TaxInvoiceDetailsForm = forwardRef(function TaxInvoiceDetailsForm(
  {
    businessInfo,
    profileExists,
    inputsEnabled = false,
    onSaved,
    hideFooter = false,
    embedded = false,
  },
  ref
) {
  const { t } = useTranslation("translation");
  const ta = useCallback((key, opts) => t(`myAccount.${key}`, opts), [t]);

  const [taxForm, setTaxForm] = useState(EMPTY_TAX_FORM);
  const [errors, setErrors] = useState({});
  const [taxSaving, setTaxSaving] = useState(false);

  useEffect(() => {
    setTaxForm(taxFormFromBusinessInfo(businessInfo));
    setErrors({});
  }, [businessInfo]);

  const setField = (field) => (event) => {
    let value = event.target.value;
    if (field === "vatNumber") {
      value = digitsOnlyVatInput(value);
    }
    setTaxForm((prev) => ({ ...prev, [field]: value }));
    if (field === "vatNumber" && errors.vatNumber) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.vatNumber;
        return next;
      });
    }
  };

  const validateTax = useCallback(() => {
    const next = {};
    const vat = taxForm.vatNumber.trim();
    if (vat && !isValidSaudiVat(vat)) {
      next.vatNumber = ta("vatInvalid");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [taxForm.vatNumber, ta]);

  const saveTaxDetails = useCallback(
    async ({ toastOnSuccess = true, requireProfile = true } = {}) => {
      if (requireProfile && !profileExists) return true;
      if (taxSaving) return false;
      if (!validateTax()) return false;
      setTaxSaving(true);
      try {
        const data = await updateTaxDetails({
          vatNumber: taxForm.vatNumber.trim(),
          nationalAddress: {
            buildingNumber: taxForm.buildingNumber.trim(),
            street: taxForm.street.trim(),
            district: taxForm.district.trim(),
            city: taxForm.city.trim(),
            postalCode: taxForm.postalCode.trim(),
            additionalNumber: taxForm.additionalNumber.trim(),
          },
        });
        if (data?.businessInfo) {
          setTaxForm(taxFormFromBusinessInfo(data.businessInfo));
          onSaved?.(data.businessInfo);
        }
        if (toastOnSuccess) {
          toast.success(ta("saveTaxDetailsSuccess"));
        }
        return true;
      } catch (err) {
        console.error("[TaxInvoiceDetailsForm.save]", err);
        const code = err.response?.data?.code;
        if (code === "invalid_vat_number") {
          setErrors({ vatNumber: ta("vatInvalid") });
          toast.error(ta("vatInvalid"));
          return false;
        }
        toast.error(
          code === "billing_profile_required" || code === "billing_profile_incomplete"
            ? ta("billingProfileRequired")
            : (err.response?.data?.message || ta("saveTaxDetailsError"))
        );
        return false;
      } finally {
        setTaxSaving(false);
      }
    },
    [onSaved, profileExists, ta, taxForm, taxSaving, validateTax]
  );

  useImperativeHandle(ref, () => ({ saveTaxDetails, validateTax }), [saveTaxDetails, validateTax]);

  const handleSave = async (event) => {
    event.preventDefault();
    await saveTaxDetails();
  };

  const disabled = !inputsEnabled;
  const showProfileRequiredHint = !profileExists && !inputsEnabled;
  const Wrapper = embedded ? "div" : "form";
  const wrapperProps = embedded
    ? { className: "my-account-biz-tax-form" }
    : { className: "my-account-biz-tax-form", onSubmit: handleSave };

  const fields = (
    <div className="my-account-biz-grid">
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.vatNumber")}</span>
        <input
          className={`my-account-biz-input${errors.vatNumber ? " is-invalid" : ""}`}
          value={taxForm.vatNumber}
          onChange={setField("vatNumber")}
          inputMode="numeric"
          autoComplete="off"
          maxLength={15}
          disabled={disabled}
          aria-invalid={Boolean(errors.vatNumber)}
        />
        {errors.vatNumber ? (
          <span className="my-account-field-error">{errors.vatNumber}</span>
        ) : null}
      </label>
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.buildingNumber")}</span>
        <input
          className="my-account-biz-input"
          value={taxForm.buildingNumber}
          onChange={setField("buildingNumber")}
          disabled={disabled}
        />
      </label>
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.street")}</span>
        <input
          className="my-account-biz-input"
          value={taxForm.street}
          onChange={setField("street")}
          disabled={disabled}
        />
      </label>
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.district")}</span>
        <input
          className="my-account-biz-input"
          value={taxForm.district}
          onChange={setField("district")}
          disabled={disabled}
        />
      </label>
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.city")}</span>
        <input
          className="my-account-biz-input"
          value={taxForm.city}
          onChange={setField("city")}
          disabled={disabled}
        />
      </label>
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.postalCode")}</span>
        <input
          className="my-account-biz-input"
          value={taxForm.postalCode}
          onChange={setField("postalCode")}
          disabled={disabled}
        />
      </label>
      <label className="my-account-biz-col">
        <span className="my-account-field__label">{ta("fields.additionalNumber")}</span>
        <input
          className="my-account-biz-input"
          value={taxForm.additionalNumber}
          onChange={setField("additionalNumber")}
          disabled={disabled}
        />
      </label>
    </div>
  );

  return (
    <div className="my-account-biz-tax-block">
      <div className="my-account-biz-subhead my-account-biz-tax-subhead">
        <div className="my-account-biz-subhead__lead">
          <div>
            <div className="my-account-biz-subhead__title">{ta("taxDetails")}</div>
            <p className="my-account-biz-subhead__hint mb-0">{ta("taxDetailsHint")}</p>
          </div>
        </div>
      </div>

      {showProfileRequiredHint ? (
        <p className="my-account-docs__hint">{ta("taxDetailsRequiresProfile")}</p>
      ) : null}

      <Wrapper {...wrapperProps}>
        {fields}
        {!hideFooter ? (
          <div className="my-account-biz-footer my-account-biz-footer--end">
            <div className="my-account-biz-actions">
              <Button color="dark" type="submit" disabled={disabled || taxSaving}>
                {taxSaving ? ta("savingTaxDetails") : ta("saveTaxDetails")}
              </Button>
            </div>
          </div>
        ) : null}
      </Wrapper>
    </div>
  );
});

export default TaxInvoiceDetailsForm;
